import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiHttpService } from '../../core/services/api-http.service';
import { ApiEndpointsService } from '../../core/services/api-endpoints.service';
import {
  EntryApi,
  GetPortfolioListResponse,
  CreatePortfolioCompanyDto,
  LegacyPortfolioCompanyPayload,
  MetricDefinitionApi,
  PeriodApi,
  PortfolioCompanyDrawerDetails,
  PortfolioCompanyDetailApi,
  PortfolioCompanyListItemApi,
  PortfolioCompanyOverviewApi,
  PortfolioCompaniesOverviewApi,
  PortfolioMessageResponse,
  UpsertPortfolioCompanyEntryDto,
  UpsertPortfolioMetricDefinitionsDto,
  UpsertPortfolioPeriodsDto,
  UpdatePortfolioCompanyDto,
} from './portfolio.types';

@Injectable({
  providedIn: 'root',
})
export class PortfolioService {
  readonly openPortfolioDrawerFlag = new BehaviorSubject(false);
  private readonly portfolioDetails = new BehaviorSubject<PortfolioCompanyDrawerDetails | null>(null);
  readonly portfolioDetailsObservable$ = this.portfolioDetails.asObservable();
  readonly refreshPortfolioList = new BehaviorSubject(false);
  readonly refreshPortfolioListObservable$ = this.refreshPortfolioList.asObservable();

  constructor(
    private apiHttpService: ApiHttpService,
    private apiEndpointsService: ApiEndpointsService
  ) {}

  public openPortfolioDrawer(): void {
    this.openPortfolioDrawerFlag.next(true);
  }

  public closePortfolioDrawer(): void {
    this.openPortfolioDrawerFlag.value &&
      this.openPortfolioDrawerFlag.next(false);
  }

  public setRefreshPortfolio(data: boolean): void {
    this.refreshPortfolioList.next(data);
  }

  public setPortfolioDetails(data: PortfolioCompanyDrawerDetails | null): void {
    this.portfolioDetails.next(data);
  }

  getPortfolioList(vehicleId: string): Observable<GetPortfolioListResponse> {
    return this.jsonResponse<GetPortfolioListResponse>(this.apiHttpService.get(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/portfolio`,
        false
      ),
      { responseType: 'json' }
    ));
  }

  getPortfolioCompanyList(fundId: string): Observable<PortfolioCompanyListItemApi[]> {
    return this.jsonResponse<PortfolioCompanyListItemApi[]>(this.apiHttpService.get(
      this.apiEndpointsService.createPortfolioCompaniesUrl(
        `funds/${fundId}/portfolio-companies`
      ),
      { responseType: 'json' }
    ));
  }

  getPortfolioCompanyOverview(fundId: string): Observable<PortfolioCompaniesOverviewApi> {
    return this.jsonResponse<PortfolioCompaniesOverviewApi>(this.apiHttpService.get(
      this.apiEndpointsService.createPortfolioCompaniesUrl(
        `funds/${fundId}/portfolio-companies/summary`
      ),
      { responseType: 'json' }
    ));
  }

  createPortfolioCompany(
    fundId: string,
    data: CreatePortfolioCompanyDto
  ): Observable<PortfolioCompanyListItemApi> {
    return this.jsonResponse<PortfolioCompanyListItemApi>(this.apiHttpService.post(
      this.apiEndpointsService.createPortfolioCompaniesUrl(
        `funds/${fundId}/portfolio-companies`
      ),
      data,
      { responseType: 'json' }
    ));
  }

  updatePortfolioCompany(
    fundId: string,
    companyId: string,
    data: UpdatePortfolioCompanyDto
  ): Observable<PortfolioCompanyListItemApi> {
    return this.jsonResponse<PortfolioCompanyListItemApi>(this.apiHttpService.patch(
      this.apiEndpointsService.createPortfolioCompaniesUrl(
        `funds/${fundId}/portfolio-companies/${companyId}`
      ),
      data,
      { responseType: 'json' }
    ));
  }

  addPortfolio(
    vehicleId: string,
    data: LegacyPortfolioCompanyPayload
  ): Observable<GetPortfolioListResponse> {
    return this.jsonResponse<GetPortfolioListResponse>(this.apiHttpService.post(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/portfolio/company`,
        false
      ),
      data,
      { responseType: 'json' }
    ));
  }

  updatePortfolio(
    portfolioCompanyId: string,
    data: LegacyPortfolioCompanyPayload
  ): Observable<PortfolioMessageResponse> {
    return this.jsonResponse<PortfolioMessageResponse>(this.apiHttpService.put(
      this.apiEndpointsService.createUrl(
        `portfolio/company/${portfolioCompanyId}`,
        false
      ),
      data,
      { responseType: 'json' }
    ));
  }

  deletePortfolio(portfolioCompanyId: string): Observable<PortfolioMessageResponse> {
    return this.jsonResponse<PortfolioMessageResponse>(this.apiHttpService.delete(
      this.apiEndpointsService.createUrl(
        `portfolio/company/${portfolioCompanyId}`,
        false
      ),
      { responseType: 'json' }
    ));
  }

  getPortfolioCompany(fundId: string, companyId: string): Observable<PortfolioCompanyDetailApi> {
    return this.jsonResponse<PortfolioCompanyDetailApi>(this.apiHttpService.get(
      this.apiEndpointsService.createPortfolioCompaniesUrl(
        `funds/${fundId}/portfolio-companies/${companyId}`
      ),
      { responseType: 'json' }
    ));
  }

  getPortfolioCompanyDetailOverview(fundId: string, companyId: string): Observable<PortfolioCompanyOverviewApi> {
    return this.jsonResponse<PortfolioCompanyOverviewApi>(this.apiHttpService.get(
      this.apiEndpointsService.createPortfolioCompaniesUrl(
        `funds/${fundId}/portfolio-companies/${companyId}/summary`
      ),
      { responseType: 'json' }
    ));
  }

  upsertPortfolioCompanyMetricDefinitions(
    fundId: string,
    companyId: string,
    data: UpsertPortfolioMetricDefinitionsDto
  ): Observable<MetricDefinitionApi[]> {
    return this.jsonResponse<MetricDefinitionApi[]>(this.apiHttpService.put(
      this.apiEndpointsService.createPortfolioCompaniesUrl(
        `funds/${fundId}/portfolio-companies/${companyId}/metrics/definitions`
      ),
      data,
      { responseType: 'json' }
    ));
  }

  upsertPortfolioCompanyPeriods(
    fundId: string,
    companyId: string,
    data: UpsertPortfolioPeriodsDto
  ): Observable<PeriodApi[]> {
    return this.jsonResponse<PeriodApi[]>(this.apiHttpService.put(
      this.apiEndpointsService.createPortfolioCompaniesUrl(
        `funds/${fundId}/portfolio-companies/${companyId}/periods`
      ),
      data,
      { responseType: 'json' }
    ));
  }

  getPortfolioCompanyPeriods(fundId: string, companyId: string): Observable<PeriodApi[]> {
    return this.jsonResponse<PeriodApi[]>(this.apiHttpService.get(
      this.apiEndpointsService.createPortfolioCompaniesUrl(
        `funds/${fundId}/portfolio-companies/${companyId}/periods`
      ),
      { responseType: 'json' }
    ));
  }

  getPortfolioCompanyEntry(
    fundId: string,
    companyId: string,
    periodId: string
  ): Observable<EntryApi> {
    return this.jsonResponse<EntryApi>(this.apiHttpService.get(
      this.apiEndpointsService.createPortfolioCompaniesUrl(
        `funds/${fundId}/portfolio-companies/${companyId}/entries/${periodId}`
      ),
      { responseType: 'json' }
    ));
  }

  upsertPortfolioCompanyEntry(
    fundId: string,
    companyId: string,
    periodId: string,
    data: UpsertPortfolioCompanyEntryDto
  ): Observable<EntryApi> {
    return this.jsonResponse<EntryApi>(this.apiHttpService.put(
      this.apiEndpointsService.createPortfolioCompaniesUrl(
        `funds/${fundId}/portfolio-companies/${companyId}/entries/${periodId}`
      ),
      data,
      { responseType: 'json' }
    ));
  }

  carryForwardPortfolioCompanyEntry(
    fundId: string,
    companyId: string,
    periodId: string
  ): Observable<EntryApi> {
    return this.jsonResponse<EntryApi>(this.apiHttpService.post(
      this.apiEndpointsService.createPortfolioCompaniesUrl(
        `funds/${fundId}/portfolio-companies/${companyId}/entries/${periodId}/carry-forward`
      ),
      {},
      { responseType: 'json' }
    ));
  }

  /** ApiHttpService does not expose JSON generics; keep that boundary in one place. */
  private jsonResponse<T>(request: Observable<unknown>): Observable<T> {
    return request as Observable<T>;
  }
}
